import { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Btn, Spinner } from '../../components/ui';
import { adminAPI } from '../../api';

const BLANK = { name: '', school: '', subjects: [], year_groups: [] };

/**
 * A patch is a middle leader's scope — the subjects and year groups they are
 * responsible for. It decides which observations they can see.
 */
export default function PatchesTab({ onChanged }) {
  const [patches, setPatches] = useState([]);
  const [schools, setSchools] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([adminAPI.patches(), adminAPI.schools(), adminAPI.options()])
      .then(([p, s, o]) => { setPatches(p.data); setSchools(s.data); setOptions(o.data); })
      .catch(err => { console.error(err); toast.error('Could not load patches.'); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const toggle = (field, value) => {
    const list = editing[field] || [];
    setEditing({
      ...editing,
      [field]: list.includes(value) ? list.filter(v => v !== value) : [...list, value],
    });
  };

  const save = async () => {
    if (!editing.name || !editing.school) {
      toast.error('A patch needs a name and a school.');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) await adminAPI.updatePatch(editing.id, editing);
      else            await adminAPI.createPatch(editing);
      toast.success(editing.id ? 'Patch updated.' : 'Patch created.');
      setEditing(null);
      load(); onChanged?.();
    } catch (err) {
      const d = err?.response?.data;
      toast.error((d && Object.values(d).flat().join(' ')) || 'Could not save that patch.');
    } finally { setSaving(false); }
  };

  const remove = async (patch) => {
    if (!window.confirm(`Delete the patch "${patch.name}"? Anyone scoped to it will be unassigned.`)) return;
    try {
      await adminAPI.deletePatch(patch.id);
      toast.success('Patch deleted.');
      load(); onChanged?.();
    } catch { toast.error('Could not delete that patch.'); }
  };

  if (loading) {
    return <div style={{ display:'flex', justifyContent:'center', padding:'32px 0' }}><Spinner /></div>;
  }

  return (
    <>
      <div className="adm-note">
        A patch is a middle leader's scope — the subjects and year groups they are
        responsible for.
      </div>

      <Btn variant="blue" onClick={() => setEditing({ ...BLANK })} style={{ marginBottom: 14 }}>
        Add patch
      </Btn>

      {patches.length === 0 ? (
        <div className="dash-empty">No patches configured yet.</div>
      ) : patches.map(p => (
        <div key={p.id} className="adm-user">
          <div className="adm-user-main">
            <div className="adm-user-name">{p.name}</div>
            <div className="adm-user-sub">{p.school_name}</div>
            <div className="adm-user-meta">
              {(p.subjects?.length ? p.subjects.join(', ') : 'All subjects')}
              {' · '}
              {(p.year_groups?.length ? p.year_groups.join(', ') : 'All year groups')}
            </div>
          </div>
          <div className="adm-user-actions">
            <button className="adm-link" onClick={() => setEditing({ ...p })}>Edit</button>
            <button className="adm-link danger" onClick={() => remove(p)}>Delete</button>
          </div>
        </div>
      ))}

      {editing && (
        <div className="sheet-backdrop" onClick={() => setEditing(null)}>
          <div className="sheet" onClick={e => e.stopPropagation()}>
            <div className="sheet-head">
              <div className="sheet-title">{editing.id ? 'Edit patch' : 'Add patch'}</div>
              <button className="sheet-close" onClick={() => setEditing(null)}>✕</button>
            </div>

            <div className="sheet-body">
              <div className="sheet-lbl">Patch name</div>
              <input className="fi" placeholder="e.g. Lower school science" value={editing.name}
                     onChange={e => setEditing({ ...editing, name: e.target.value })} />

              <div className="sheet-lbl">School</div>
              <select className="fi" value={editing.school || ''}
                      onChange={e => setEditing({ ...editing, school: e.target.value })}>
                <option value="">Select school…</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.dropdown_label}</option>)}
              </select>

              <div className="sheet-lbl">
                Subjects <span className="adm-hint">none selected means all subjects</span>
              </div>
              <div className="adm-chips">
                {options?.subjects.map(s => (
                  <button key={s} type="button"
                          className={`adm-chip${editing.subjects?.includes(s) ? ' on' : ''}`}
                          onClick={() => toggle('subjects', s)}>
                    {s}
                  </button>
                ))}
              </div>

              <div className="sheet-lbl">
                Year groups <span className="adm-hint">none selected means all year groups</span>
              </div>
              <div className="adm-chips">
                {options?.grades.map(g => (
                  <button key={g} type="button"
                          className={`adm-chip${editing.year_groups?.includes(g) ? ' on' : ''}`}
                          onClick={() => toggle('year_groups', g)}>
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div className="sheet-foot">
              <Btn variant="dark" onClick={save} loading={saving}>
                {editing.id ? 'Save changes' : 'Create patch'}
              </Btn>
              <button className="sheet-cancel" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
